import {EventSubscriber, HasEventName} from "@/modules/base/events";
import {EvaluateDayEvent} from "@/modules/season/events/EvaluateDayEvent";
import {inject, injectable} from "inversify";
import chance from "@/modules/base/services/chance";
import {Team} from "@/modules/team/entity";
import {YouthPlayer} from "@/modules/youth/entity";
import {Recruiter} from "@/modules/youth/services/Recruiter";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import {AfterSeasonEvaluationEvent} from "@/modules/season/events/AfterSeasonEvaluationEvent";
import {YouthPlayerTransferator} from "@/modules/youth/services/YouthPlayerTransferator";

@injectable()
export class RecruitYouthPlayerEventSubscriber implements EventSubscriber {
  constructor(
    @inject(Recruiter) private recruiter: Recruiter,
    @inject(ConfigRepository) private config: ConfigRepository,
    @inject(YouthPlayerTransferator) private youthPlayerTransferator: YouthPlayerTransferator,
  ) {}

  priority(): number {
    return 0;
  }

  init(): { event: HasEventName; callback: (event: any) => void }[] {
    return [
      {event: EvaluateDayEvent, callback: this.onEvaluateDay},
      {event: AfterSeasonEvaluationEvent, callback: this.onAfterSeasonEvaluationEvent},
    ];
  }

  async onAfterSeasonEvaluationEvent(event: AfterSeasonEvaluationEvent): Promise<void> {
    const players = await YouthPlayer
      .createQueryBuilder('player')
      .innerJoinAndSelect('player.recruitedBy', 'recruitedByTeam')
      .innerJoinAndSelect('player.country', 'country')
      .innerJoinAndSelect('country.region', 'region')
      .getMany();

    await players.forEachAsync(async (p: YouthPlayer) => {
      await this.youthPlayerTransferator.transfer(p, p.recruitedBy!);
    });
  }

  async onEvaluateDay(event: EvaluateDayEvent): Promise<void> {
    if (chance.bool({likelihood: this.config.get('youth.otherTeamRecruitmentLikelihood')})) {
      console.debug('try to recruit youth player');

      const players = await YouthPlayer.createQueryBuilder('player')
        .leftJoin('player.recruitedBy', 'team')
        .where('team.id is null')
        .getMany();

      if (players.length === 0) {
        return;
      }

      const teams = await Team.createQueryBuilder('team')
        .where({user: false})
        .getMany();

      if (teams.length === 0) {
        return;
      }

      const team = chance.pickone(teams);
      const player = chance.pickone(players);

      if (team && player) {
        await this.recruiter.recruit(player, team);
      }
    }
  }
}